import { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  HStack,
  Icon,
  useColorMode,
  VStack,
  Alert,
  AlertIcon,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react'
import { Copy, Check, LogOut } from 'lucide-react'
import { toast } from 'sonner'
import { useCopyToClipboard } from '@/lib/hooks/useCopyToClipboard'
import { getStoredApiKey, clearStoredApiKey, getDrafts, clearDraftsForApiKey, clearEmptyDrafts, convertDraftsToAnonymous } from '@/lib/storage/local-storage'

interface SignOutConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onSignOut: () => void
}

export function SignOutConfirmModal({ isOpen, onClose, onSignOut }: SignOutConfirmModalProps) {
  const { colorMode } = useColorMode()
  const { copy, hasCopied } = useCopyToClipboard()
  const [draftOption, setDraftOption] = useState<'keep' | 'clear-empty' | 'clear-all'>('clear-empty')
  const [draftCount, setDraftCount] = useState(0)
  const [emptyDraftCount, setEmptyDraftCount] = useState(0)
  const apiKey = getStoredApiKey()

  // Count drafts for current API key when modal opens
  useEffect(() => {
    if (isOpen) {
      const drafts = getDrafts()
      const emptyCount = drafts.filter(d => d.content.trim().length === 0).length
      setDraftCount(drafts.length)
      setEmptyDraftCount(emptyCount)

      // Smart default: if all drafts are empty, suggest clear-all
      // Otherwise suggest clear-empty
      if (drafts.length === 0) {
        setDraftOption('clear-all')
      } else if (emptyCount === drafts.length) {
        setDraftOption('clear-all')
      } else {
        setDraftOption('clear-empty')
      }
    }
  }, [isOpen])

  const handleCopyKey = () => {
    if (apiKey) {
      copy(apiKey, 'apiKey', 'Secret key copied to clipboard')
    }
  }

  const handleSignOut = () => {
    const currentApiKey = getStoredApiKey()

    // Handle drafts based on user selection
    if (draftOption === 'clear-all') {
      // Remove all drafts for this API key and any stale anonymous drafts
      clearDraftsForApiKey(currentApiKey)
      clearDraftsForApiKey(null)
    } else if (draftOption === 'clear-empty' && currentApiKey) {
      // First convert to anonymous, THEN clear empty drafts
      // This ensures we're clearing empty drafts in the anonymous context
      convertDraftsToAnonymous(currentApiKey)
      clearEmptyDrafts()
    } else if (draftOption === 'keep' && currentApiKey) {
      // Convert all drafts to anonymous so they remain accessible after sign-out
      convertDraftsToAnonymous(currentApiKey)
    }

    clearStoredApiKey()
    window.dispatchEvent(new Event('apikey-changed'))
    toast.success('Signed out successfully')
    onSignOut()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={LogOut} boxSize={5} />
            <Text>Sign Out</Text>
          </HStack>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Make sure you have your secret key saved. You won't be able to access your shares without it.
              </Text>
            </Alert>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Your Secret Key
              </Text>
              <HStack>
                <Box
                  flex={1}
                  p={3}
                  bg={colorMode === 'dark' ? 'stone.700' : 'stone.100'}
                  borderRadius="md"
                  fontFamily="mono"
                  fontSize="sm"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {apiKey ? `${apiKey.slice(0, 12)}...${apiKey.slice(-8)}` : '-'}
                </Box>
                <Button
                  size="sm"
                  leftIcon={<Icon as={hasCopied('apiKey') ? Check : Copy} boxSize={4} />}
                  onClick={handleCopyKey}
                  colorScheme={hasCopied('apiKey') ? 'green' : 'gray'}
                  variant="outline"
                >
                  {hasCopied('apiKey') ? 'Copied' : 'Copy'}
                </Button>
              </HStack>
            </Box>

            {draftCount > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  What should happen to your {draftCount} draft{draftCount > 1 ? 's' : ''}?
                </Text>
                <RadioGroup value={draftOption} onChange={(val) => setDraftOption(val as typeof draftOption)}>
                  <Stack spacing={2}>
                    <Radio value="keep" size="sm">
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">Keep all drafts</Text>
                        <Text fontSize="xs" color="stone.500">
                          Continue working on them anonymously
                        </Text>
                      </VStack>
                    </Radio>
                    <Radio value="clear-empty" size="sm">
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">Clear empty drafts only</Text>
                        <Text fontSize="xs" color="stone.500">
                          {emptyDraftCount > 0
                            ? `Remove ${emptyDraftCount} empty draft${emptyDraftCount > 1 ? 's' : ''}, keep ${draftCount - emptyDraftCount} with content`
                            : 'All drafts have content, none will be removed'}
                        </Text>
                      </VStack>
                    </Radio>
                    <Radio value="clear-all" size="sm">
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">Clear all drafts</Text>
                        <Text fontSize="xs" color="stone.500">
                          Start fresh with a clean slate
                        </Text>
                      </VStack>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2}>
            <Button size="sm" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              onClick={handleSignOut}
            >
              Sign Out
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
